import React, { useEffect, useState } from 'react';
import { Card, Typography, Space, Spin, Row, Col, Statistic } from 'antd';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { analyticsApi } from '../services/api';

const { Title } = Typography;

interface ChannelData {
  channel_group: string;
  channel_name_ja: string;
  total_users: number;
  new_users: number;
  sessions: number;
  percentage: number;
  color?: string;
}

interface UserAcquisitionChartProps {
  selectedDate: string;
}

const UserAcquisitionChart: React.FC<UserAcquisitionChartProps> = ({ selectedDate }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [channelData, setChannelData] = useState<ChannelData[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);

  // 颜色配置
  const colors = [
    '#1890ff', // 蓝色 - Direct
    '#52c41a', // 绿色 - Organic Search
    '#faad14', // 金色 - Paid Search
    '#f5222d', // 红色 - Social
    '#722ed1', // 紫色 - Email
    '#fa8c16', // 橙色 - Referral
    '#13c2c2', // 青色 - Affiliate
    '#eb2f96', // 洋红色 - Display
    '#fadb14', // 黄色 - Video
    '#a0d911'  // 青绿色 - Others
  ];

  useEffect(() => {
    loadUserAcquisitionData();
  }, [selectedDate]);

  const loadUserAcquisitionData = async () => {
    try {
      setLoading(true);
      
      // 检查Google Analytics是否可用
      const availabilityResponse = await analyticsApi.checkGoogleAnalyticsAvailability();
      setIsAvailable(availabilityResponse.available);
      
      if (!availabilityResponse.available) {
        setChannelData([]);
        setTotalUsers(0);
        return;
      }
      
      // 获取渠道组分布数据
      const response = await analyticsApi.getUserAcquisitionData(selectedDate);
      
      if (response.error) {
        console.error('获取用户获取数据失败:', response.error);
        setChannelData([]);
        setTotalUsers(0);
        return;
      }
      
      // 为数据添加颜色
      const dataWithColors = response.channels.map((item: ChannelData, index: number) => ({
        ...item,
        color: colors[index % colors.length]
      }));
      
      setChannelData(dataWithColors);
      setTotalUsers(response.total_users || 0);
      
      console.log('用户获取数据:', response);
    } catch (error) {
      console.error('Failed to load user acquisition data:', error);
      setChannelData([]);
      setTotalUsers(0);
      setIsAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const renderUnavailableMessage = () => (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      color: '#999',
      textAlign: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
      <div style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 'bold' }}>
        Google Analytics データなし
      </div>
      <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
        Google Analytics 4の設定が必要です<br/>
        または、データがまだ収集されていません
      </div>
    </div>
  );

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>新規ユーザー獲得状況</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>New User Acquisition by Channel</span>
        </Space>
      }
      style={{ height: '480px' }}
    >
      <div style={{ height: 'calc(100% - 60px)', padding: '16px' }}>
        <Spin spinning={loading}>
          {!isAvailable ? (
            renderUnavailableMessage()
          ) : channelData.length > 0 ? (
            <Row gutter={[16, 16]} style={{ height: '100%' }}>
              {/* 饼图区域 */}
              <Col xs={24} sm={14} style={{ height: '100%' }}>
                <div style={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  {/* 饼图 */}
                  <ResponsiveContainer width="100%" height={360}>
                    <PieChart>
                      <Pie
                        data={channelData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={50}
                        dataKey="new_users"
                        label={false}
                      >
                        {channelData.map((entry: ChannelData, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }: any) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                  {data.channel_name_ja}
                                </div>
                                <div style={{ color: '#666' }}>
                                  新規ユーザー: {data.new_users}人 ({data.percentage}%)
                                </div>
                                <div style={{ color: '#666' }}>
                                  総ユーザー: {data.total_users}人
                                </div>
                                <div style={{ color: '#666' }}>
                                  セッション: {data.sessions}回
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              
              {/* 列表区域 */}
              <Col xs={24} sm={10} style={{ height: '100%' }}>
                <div style={{ padding: '8px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Title level={5} style={{ marginBottom: '8px', fontSize: '14px' }}>チャネル別詳細</Title>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {channelData.map((item: ChannelData, index: number) => (
                      <div key={item.channel_group} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        background: index === 0 ? '#f6ffed' : '#fafafa',
                        borderRadius: '4px',
                        border: index === 0 ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                        marginBottom: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <div style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: item.color,
                            borderRadius: '2px',
                            marginRight: '6px',
                            flexShrink: 0
                          }}></div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '12px', 
                              fontWeight: 'bold',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {item.channel_name_ja}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              {item.channel_group}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: '8px', flexShrink: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {item.new_users}人
                          </div>
                          <div style={{ fontSize: '10px', color: '#666' }}>
                            {item.percentage}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#999'
            }}>
              {loading ? '読み込み中...' : 'データがありません'}
            </div>
          )}
        </Spin>
      </div>
    </Card>
  );
};

export default UserAcquisitionChart;